import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertTriangle, Scale, FileCheck, Calendar, Plus, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface License {
  id: string;
  name: string;
  type: string;
  issuing_authority: string;
  license_number: string;
  issue_date: string;
  expiry_date: string;
  status: 'active' | 'expired' | 'pending_renewal' | 'suspended';
  renewal_cost: number;
  currency: string;
  requirements: string[];
  notes: string;
  created_at: string;
}

interface Requirement {
  id: string;
  title: string;
  description: string;
  type: 'license' | 'certification' | 'documentation' | 'procedure';
  due_date: string;
  status: 'compliant' | 'non_compliant' | 'pending' | 'in_progress';
  assigned_to: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  created_at: string;
}

export default function LegalCompliancePage() {
  const [isNewLicenseOpen, setIsNewLicenseOpen] = useState(false);
  const [isNewRequirementOpen, setIsNewRequirementOpen] = useState(false);
  const [newLicense, setNewLicense] = useState({
    name: "",
    type: "",
    issuing_authority: "",
    license_number: "",
    issue_date: "",
    expiry_date: "",
    renewal_cost: 0,
    currency: "SAR",
    notes: ""
  });
  const [newRequirement, setNewRequirement] = useState({
    title: "",
    description: "",
    type: "license",
    due_date: "",
    priority: "medium",
    assigned_to: ""
  });

  const queryClient = useQueryClient();

  const { data: licenses = [] } = useQuery({
    queryKey: ['licenses'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('licenses' as any)
        .select('*')
        .order('expiry_date', { ascending: true });
      
      if (error) throw error;
      return (data as unknown as License[]) || [];
    }
  });

  const { data: requirements = [] } = useQuery({
    queryKey: ['compliance-requirements'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('compliance_requirements' as any)
        .select('*')
        .order('due_date', { ascending: true });
      
      if (error) throw error;
      return (data as unknown as Requirement[]) || [];
    }
  });

  const createLicenseMutation = useMutation({
    mutationFn: async (license: any) => {
      const status = new Date(license.expiry_date) < new Date() ? 'expired' : 'active';
      const { data, error } = await supabase
        .from('licenses' as any)
        .insert([{ ...license, status }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
      setIsNewLicenseOpen(false);
      setNewLicense({
        name: "", type: "", issuing_authority: "", license_number: "",
        issue_date: "", expiry_date: "", renewal_cost: 0, currency: "SAR", notes: ""
      });
      toast.success("تم إضافة الترخيص بنجاح");
    }
  });

  const createRequirementMutation = useMutation({
    mutationFn: async (requirement: any) => {
      const { data, error } = await supabase
        .from('compliance_requirements' as any)
        .insert([{ ...requirement, status: 'pending' }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance-requirements'] });
      setIsNewRequirementOpen(false);
      setNewRequirement({
        title: "", description: "", type: "license", 
        due_date: "", priority: "medium", assigned_to: ""
      });
      toast.success("تم إضافة المتطلب بنجاح");
    }
  });

  const updateRequirementStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from('compliance_requirements' as any)
        .update({ status })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance-requirements'] });
      toast.success("تم تحديث حالة المتطلب");
    }
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': 
      case 'compliant': 
        return 'bg-green-100 text-green-800';
      case 'expired': 
      case 'non_compliant': 
        return 'bg-red-100 text-red-800';
      case 'pending_renewal': 
      case 'pending': 
        return 'bg-yellow-100 text-yellow-800';
      case 'suspended': 
      case 'in_progress': 
        return 'bg-orange-100 text-orange-800';
      default: 
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDaysUntilExpiry = (expiryDate: string) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const expiringSoon = licenses.filter(license => {
    const days = getDaysUntilExpiry(license.expiry_date);
    return days <= 30 && days > 0;
  }).length;

  const expiredLicenses = licenses.filter(license => 
    getDaysUntilExpiry(license.expiry_date) <= 0
  ).length;

  const nonCompliantRequirements = requirements.filter(req => 
    req.status === 'non_compliant'
  ).length;

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-7xl">
      <div className="flex items-center gap-4 mb-8">
        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-lg">
          <Scale className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">الامتثال القانوني</h1>
          <p className="text-muted-foreground mt-1">
            متطلبات التراخيص والامتثال القانوني
          </p>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">التراخيص النشطة</CardTitle>
            <FileCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {licenses.filter(l => l.status === 'active').length}
            </div>
            <p className="text-xs text-muted-foreground">من أصل {licenses.length}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">تنتهي قريباً</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{expiringSoon}</div>
            <p className="text-xs text-muted-foreground">خلال 30 يوم</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">منتهية الصلاحية</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{expiredLicenses}</div>
            <p className="text-xs text-muted-foreground">تحتاج تجديد</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">غير متوافقة</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{nonCompliantRequirements}</div>
            <p className="text-xs text-muted-foreground">متطلبات</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="licenses" className="space-y-6">
        <TabsList>
          <TabsTrigger value="licenses">التراخيص</TabsTrigger>
          <TabsTrigger value="requirements">متطلبات الامتثال</TabsTrigger>
        </TabsList>

        <TabsContent value="licenses">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">التراخيص والشهادات</h2>
            <Dialog open={isNewLicenseOpen} onOpenChange={setIsNewLicenseOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 ml-2" />
                  ترخيص جديد
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>إضافة ترخيص جديد</DialogTitle>
                  <DialogDescription>
                    أدخل تفاصيل الترخيص أو الشهادة
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">اسم الترخيص *</label>
                      <Input
                        value={newLicense.name}
                        onChange={(e) => setNewLicense({ ...newLicense, name: e.target.value })}
                        placeholder="أدخل اسم الترخيص"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">النوع</label>
                      <Select value={newLicense.type} onValueChange={(value) => setNewLicense({ ...newLicense, type: value })}>
                        <SelectTrigger>
                          <SelectValue placeholder="اختر النوع" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="business">ترخيص تجاري</SelectItem>
                          <SelectItem value="tourism">ترخيص سياحي</SelectItem>
                          <SelectItem value="professional">ترخيص مهني</SelectItem>
                          <SelectItem value="certification">شهادة</SelectItem>
                          <SelectItem value="permit">تصريح</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">الجهة المصدرة</label>
                      <Input
                        value={newLicense.issuing_authority}
                        onChange={(e) => setNewLicense({ ...newLicense, issuing_authority: e.target.value })}
                        placeholder="أدخل اسم الجهة"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">رقم الترخيص</label>
                      <Input
                        value={newLicense.license_number}
                        onChange={(e) => setNewLicense({ ...newLicense, license_number: e.target.value })}
                        placeholder="أدخل رقم الترخيص"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">تاريخ الإصدار</label>
                      <Input
                        type="date"
                        value={newLicense.issue_date}
                        onChange={(e) => setNewLicense({ ...newLicense, issue_date: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">تاريخ الانتهاء</label>
                      <Input
                        type="date"
                        value={newLicense.expiry_date}
                        onChange={(e) => setNewLicense({ ...newLicense, expiry_date: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">تكلفة التجديد</label>
                      <Input
                        type="number"
                        value={newLicense.renewal_cost}
                        onChange={(e) => setNewLicense({ ...newLicense, renewal_cost: parseFloat(e.target.value) })}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">العملة</label>
                      <Select value={newLicense.currency} onValueChange={(value) => setNewLicense({ ...newLicense, currency: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="SAR">ريال سعودي</SelectItem>
                          <SelectItem value="USD">دولار أمريكي</SelectItem>
                          <SelectItem value="EUR">يورو</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">ملاحظات</label>
                    <Textarea
                      value={newLicense.notes}
                      onChange={(e) => setNewLicense({ ...newLicense, notes: e.target.value })}
                      placeholder="أدخل أي ملاحظات إضافية"
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button 
                      onClick={() => createLicenseMutation.mutate(newLicense)}
                      disabled={!newLicense.name || createLicenseMutation.isPending}
                    >
                      إضافة الترخيص
                    </Button>
                    <Button variant="outline" onClick={() => setIsNewLicenseOpen(false)}>
                      إلغاء
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-4">
            {licenses.map((license) => {
              const daysUntilExpiry = getDaysUntilExpiry(license.expiry_date);
              const isExpiring = daysUntilExpiry <= 30 && daysUntilExpiry > 0;
              const isExpired = daysUntilExpiry <= 0;

              return (
                <Card key={license.id} className={isExpired ? "border-red-200" : isExpiring ? "border-yellow-200" : ""}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg mb-1">{license.name}</CardTitle>
                        <CardDescription>
                          {license.issuing_authority} • رقم: {license.license_number}
                        </CardDescription>
                        <div className="flex gap-2 mt-2">
                          <Badge className={getStatusColor(license.status)}>
                            {license.status === 'active' && 'نشط'}
                            {license.status === 'expired' && 'منتهي'}
                            {license.status === 'pending_renewal' && 'قيد التجديد'}
                            {license.status === 'suspended' && 'معلق'}
                          </Badge>
                          {license.type && (
                            <Badge variant="outline">
                              {license.type === 'business' && 'ترخيص تجاري'}
                              {license.type === 'tourism' && 'ترخيص سياحي'}
                              {license.type === 'professional' && 'ترخيص مهني'}
                              {license.type === 'certification' && 'شهادة'}
                              {license.type === 'permit' && 'تصريح'}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="text-left">
                        <div className="text-sm font-medium">
                          ينتهي في: {new Date(license.expiry_date).toLocaleDateString('ar-SA')}
                        </div>
                        <div className={`text-sm ${isExpired ? 'text-red-600' : isExpiring ? 'text-yellow-600' : 'text-muted-foreground'}`}>
                          {isExpired ? `متأخر ${Math.abs(daysUntilExpiry)} يوم` : 
                           isExpiring ? `${daysUntilExpiry} يوم متبقي` : 
                           `${daysUntilExpiry} يوم متبقي`}
                        </div>
                        {license.renewal_cost > 0 && (
                          <div className="text-sm text-muted-foreground">
                            تكلفة التجديد: {license.renewal_cost} {license.currency}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  {license.notes && (
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{license.notes}</p>
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="requirements">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">متطلبات الامتثال</h2>
            <Dialog open={isNewRequirementOpen} onOpenChange={setIsNewRequirementOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 ml-2" />
                  متطلب جديد
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>إضافة متطلب امتثال جديد</DialogTitle>
                  <DialogDescription>
                    أدخل تفاصيل المتطلب القانوني أو التنظيمي
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">عنوان المتطلب *</label>
                    <Input
                      value={newRequirement.title}
                      onChange={(e) => setNewRequirement({ ...newRequirement, title: e.target.value })}
                      placeholder="أدخل عنوان المتطلب"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">النوع</label>
                      <Select value={newRequirement.type} onValueChange={(value) => setNewRequirement({ ...newRequirement, type: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="license">ترخيص</SelectItem>
                          <SelectItem value="certification">شهادة</SelectItem>
                          <SelectItem value="documentation">وثائق</SelectItem>
                          <SelectItem value="procedure">إجراء</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-2 block">الأولوية</label>
                      <Select value={newRequirement.priority} onValueChange={(value) => setNewRequirement({ ...newRequirement, priority: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">منخفضة</SelectItem>
                          <SelectItem value="medium">متوسطة</SelectItem>
                          <SelectItem value="high">عالية</SelectItem>
                          <SelectItem value="critical">حرجة</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">الموعد النهائي</label>
                    <Input
                      type="date"
                      value={newRequirement.due_date}
                      onChange={(e) => setNewRequirement({ ...newRequirement, due_date: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">الوصف</label>
                    <Textarea
                      value={newRequirement.description}
                      onChange={(e) => setNewRequirement({ ...newRequirement, description: e.target.value })}
                      placeholder="وصف المتطلب والخطوات المطلوبة"
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button 
                      onClick={() => createRequirementMutation.mutate(newRequirement)}
                      disabled={!newRequirement.title || createRequirementMutation.isPending}
                    >
                      إضافة المتطلب
                    </Button>
                    <Button variant="outline" onClick={() => setIsNewRequirementOpen(false)}>
                      إلغاء
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-4">
            {requirements.map((requirement) => (
              <Card key={requirement.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-1">{requirement.title}</CardTitle>
                      <CardDescription>{requirement.description}</CardDescription>
                      <div className="flex gap-2 mt-2">
                        <Badge className={getStatusColor(requirement.status)}>
                          {requirement.status === 'compliant' && 'متوافق'}
                          {requirement.status === 'non_compliant' && 'غير متوافق'}
                          {requirement.status === 'pending' && 'معلق'}
                          {requirement.status === 'in_progress' && 'قيد التنفيذ'}
                        </Badge>
                        <Badge className={getPriorityColor(requirement.priority)}>
                          {requirement.priority === 'critical' && 'حرجة'}
                          {requirement.priority === 'high' && 'عالية'}
                          {requirement.priority === 'medium' && 'متوسطة'}
                          {requirement.priority === 'low' && 'منخفضة'}
                        </Badge>
                        <Badge variant="outline">
                          {requirement.type === 'license' && 'ترخيص'}
                          {requirement.type === 'certification' && 'شهادة'}
                          {requirement.type === 'documentation' && 'وثائق'}
                          {requirement.type === 'procedure' && 'إجراء'}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-left">
                      {requirement.due_date && (
                        <div className="text-sm text-muted-foreground">
                          الموعد النهائي: {new Date(requirement.due_date).toLocaleDateString('ar-SA')}
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Select 
                      value={requirement.status} 
                      onValueChange={(value) => updateRequirementStatusMutation.mutate({ id: requirement.id, status: value })}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">معلق</SelectItem>
                        <SelectItem value="in_progress">قيد التنفيذ</SelectItem>
                        <SelectItem value="compliant">متوافق</SelectItem>
                        <SelectItem value="non_compliant">غير متوافق</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}